/**
 * Keep Afterpay / Square on-site messaging in sync with the selected variant.
 *
 * Why this exists
 * ---------------
 * The Afterpay On-Site Messaging app block watches PerformanceObserver resource
 * entries for section renders that include BOTH `section_id` and `variant`.
 * Horizon’s variant picker fetches with `option_values` only (no section_id /
 * variant query pair on product pages), so the app never updates data-amount.
 * Price, URL, and sticky ATC all update; the financing quote does not.
 *
 * That leaves a consumer-credit figure calculated from a different total —
 * unacceptable on a medical-device PDP. We listen to Horizon’s variant:update
 * and push the selected variant’s price into product-page square-placement nodes.
 *
 * Square’s <square-placement> observes data-amount and re-renders when it changes
 * (attributeChangedCallback → renderPlacementByType). We also clone-replace as a
 * fallback when the attribute was already equal after normalization.
 */
import { ThemeEvents } from '@theme/events';

/**
 * Format cents → Afterpay data-amount string (e.g. 347000 → "3470.00").
 * Square’s parser strips commas and reads whole + fractional dollars.
 * @param {number} cents
 * @returns {string | null}
 */
function formatAmountFromCents(cents) {
  if (typeof cents !== 'number' || !Number.isFinite(cents) || cents < 0) {
    return null;
  }
  return (cents / 100).toFixed(2);
}

/**
 * Parse Afterpay-style money strings to cents.
 * @param {string} value
 * @returns {number | null}
 */
function parseMoneyToCents(value) {
  if (!value || typeof value !== 'string') return null;
  const cleaned = value.replace(/,/g, '').trim();
  const match = cleaned.match(/^(\d+)(?:\.(\d{1,2}))?/);
  if (!match) return null;
  const dollars = Number(match[1]);
  const fraction = match[2] ? Number((match[2] + '0').slice(0, 2)) : 0;
  if (!Number.isFinite(dollars)) return null;
  return dollars * 100 + fraction;
}

/**
 * @param {Element} el
 * @returns {boolean}
 */
function isProductPagePlacement(el) {
  // Prefer placements in product chrome / Afterpay app blocks.
  if (el.closest('.product-information, .product-details, product-information, [id*="afterpay"], .shopify-app-block')) {
    return true;
  }
  // Typical PDP has a single placement — update it.
  return document.querySelectorAll('square-placement[data-amount]').length === 1;
}

/**
 * Write a new amount onto a placement and remount so Square recomputes.
 * Financing quotes must never lag the displayed price — prefer a clean remount
 * over relying solely on attribute observation (which the app’s own Horizon
 * integration already fails to drive).
 * @param {HTMLElement} el
 * @param {string} amount
 */
function setPlacementAmount(el, amount) {
  const current = el.getAttribute('data-amount');
  const currentCents = current ? parseMoneyToCents(current) : null;
  const nextCents = parseMoneyToCents(amount);

  if (currentCents != null && nextCents != null && currentCents === nextCents) {
    if (current !== amount) {
      el.setAttribute('data-amount', amount);
    }
    return;
  }

  const parent = el.parentNode;
  if (!parent) {
    el.setAttribute('data-amount', amount);
    return;
  }

  // Remount: new node with correct data-amount so Square mounts against the
  // live total (not the initial page-load variant).
  const clone = /** @type {HTMLElement} */ (el.cloneNode(false));
  clone.setAttribute('data-amount', amount);
  parent.replaceChild(clone, el);
}

/**
 * Update all product-page Afterpay placements to a new amount.
 * @param {string} amount
 */
function applyAmountToPlacements(amount) {
  const placements = document.querySelectorAll('square-placement[data-amount]');
  if (!placements.length) return;

  placements.forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    if (!isProductPagePlacement(el)) return;
    setPlacementAmount(el, amount);
  });
}

/**
 * Prefer live variant price; fall back to server-rendered placement HTML.
 * @param {Event & { detail?: any }} event
 */
function handleVariantUpdate(event) {
  const detail = event.detail;
  if (!detail) return;

  const resource = detail.resource;
  let amount = null;

  if (resource && typeof resource.price === 'number') {
    amount = formatAmountFromCents(resource.price);
  }

  if (!amount && detail.data?.html instanceof Document) {
    const serverPlacement = detail.data.html.querySelector('square-placement[data-amount]');
    const serverAmount = serverPlacement?.getAttribute('data-amount');
    if (serverAmount) {
      const cents = parseMoneyToCents(serverAmount);
      amount = cents != null ? formatAmountFromCents(cents) : serverAmount.replace(/,/g, '');
    }
  }

  if (!amount) return;
  applyAmountToPlacements(amount);
}

/**
 * History back/forward: re-sync from the current variant-picker JSON payload.
 */
function handlePopState() {
  const picker = document.querySelector('variant-picker script[type="application/json"]');
  if (!picker?.textContent) return;
  try {
    const variant = JSON.parse(picker.textContent);
    if (typeof variant?.price === 'number') {
      const amount = formatAmountFromCents(variant.price);
      if (amount) applyAmountToPlacements(amount);
    }
  } catch {
    // ignore malformed JSON
  }
}

document.addEventListener(ThemeEvents.variantUpdate, handleVariantUpdate);
window.addEventListener('popstate', handlePopState);
